// src/redux/slices/inventory.slice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { InventoryRecordsService, LocationsService, CreateInventoryRecordDto } from '../../api';

// Define types
interface Product {
  id: number;
  name: string;
  code: string;
}

interface Branch {
  id: number;
  name: string;
}

interface InventoryState {
  branches: Branch[];
  products: Product[];
  loading: boolean;
  error: string | null;
  success: string | null;
}

// Initial state
const initialState: InventoryState = {
  branches: [],
  products: [],
  loading: false,
  error: null,
  success: null,
};

// Async thunks
export const fetchBranches = createAsyncThunk(
  'inventory/fetchBranches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await LocationsService.locationsControllerFindAll();

      // Map to the expected format if needed
      return Array.isArray(response)
        ? response.map((location) => ({
            id: location.id,
            name: location.name,
          }))
        : [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch branches');
    }
  },
);

export const createInventoryRecord = createAsyncThunk(
  'inventory/createInventoryRecord',
  async (data: CreateInventoryRecordDto, { rejectWithValue }) => {
    try {
      const response = await InventoryRecordsService.inventoryRecordsControllerCreate(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create inventory record');
    }
  },
);

// Slice
const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch branches
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create inventory record
      .addCase(createInventoryRecord.pending, (state) => {
        state.loading = true;
      })
      .addCase(createInventoryRecord.fulfilled, (state) => {
        state.loading = false;
        state.success = 'Inventory record created successfully';
      })
      .addCase(createInventoryRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearErrors, clearSuccess } = inventorySlice.actions;

export default inventorySlice.reducer;
